import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import {
    X, Globe, ExternalLink
} from "lucide-react";

import { fetchMeta } from "../utils/fetchMeta";

/* ---------------------- 🔹 Modal ---------------------- */
export default function CitationModal({ citations, onClose }) {
    const [metaList, setMetaList] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        Promise.all(citations.map(fetchMeta))
            .then(setMetaList)
            .finally(() => setLoading(false));
    }, [citations]);

    const modal = (
        <div className="fixed inset-0 bg-black/25 backdrop-blur-sm z-[1200] flex justify-end animate-fadeIn">
            <div className="bg-white w-[420px] h-full shadow-xl border-l border-slate-200 overflow-y-auto animate-slideInRight">
                {/* Header */}
                <div className="sticky top-0 z-10 bg-white/95 backdrop-blur-md border-b border-slate-200 flex items-center justify-between px-4 py-3">
                    <div className="font-semibold text-slate-800 flex items-center gap-2">
                        <Globe size={18} />
                        {loading ? "กำลังโหลด..." : `${metaList.length} Sources`}
                    </div>
                    <button
                        onClick={onClose}
                        className="hover:bg-slate-100 rounded-lg p-1 transition text-slate-500"
                    >
                        <X size={18} />
                    </button>
                </div>

                {/* Content */}
                <div className="p-4 space-y-3">
                    {loading ? (
                        /* 🌀 Skeleton Loading */
                        Array.from({ length: 5 }).map((_, i) => (
                            <div
                                key={i}
                                className="border border-slate-200 rounded-xl p-4 animate-pulse bg-slate-50"
                            >
                                <div className="flex items-center gap-3">
                                    <div className="w-8 h-8 bg-slate-200 rounded-md"></div>
                                    <div className="flex-1 space-y-2">
                                        <div className="h-4 bg-slate-200 rounded w-3/4"></div>
                                        <div className="h-3 bg-slate-100 rounded w-1/2"></div>
                                    </div>
                                </div>
                            </div>
                        ))
                    ) : metaList.length === 0 ? (
                        <div className="text-slate-500 text-sm text-center py-10">
                            ไม่พบข้อมูลอ้างอิง
                        </div>
                    ) : (
                        /* ✅ Real Data */
                        metaList.map((m, i) => (
                            <a
                                key={i}
                                href={m.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="block border border-slate-200 rounded-xl hover:bg-slate-50 transition p-4 bg-white"
                            >
                                <div className="flex items-center gap-3">
                                    <img
                                        src={m.image}
                                        className="w-8 h-8 rounded-md object-cover bg-white"
                                        alt="favicon"
                                        onError={(e) => {
                                            e.currentTarget.style.visibility = "hidden";
                                        }}
                                    />
                                    <div className="flex-1">
                                        <div className="font-medium text-slate-800 break-all">
                                            {m.title}
                                        </div>
                                        <div className="text-sm text-slate-500 line-clamp-2">
                                            {m.description}
                                        </div>
                                        <div className="text-xs text-indigo-600 mt-1">
                                            {m.site}
                                        </div>
                                    </div>
                                    <ExternalLink size={16} className="text-slate-400" />
                                </div>
                            </a>
                        ))
                    )}
                </div>
            </div>
        </div>
    );

    if (typeof document === "undefined") return modal;
    return createPortal(modal, document.body);
}
